import { t } from '@lingui/core/macro';
import {
  Box,
  CircularProgress,
  InputBase,
  Modal,
  Paper,
  Stack,
  Typography,
  useAutocomplete,
} from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { useIndexQuery } from 'api';
import { OfferIconMUI } from 'components/icons/OfferIconMUI';
import { SearchIconMUI } from 'components/icons/SearchIconMUI';
import { WarningIconMUI2 } from 'components/icons/WarningIconMUI2';
import SvgIcon from 'components/SvgIcon';
import { useKeyboardShortcut } from 'hooks/useKeyboardShortcut';
import { useSearchState } from 'hooks/useSearchState';
import {
  Fragment,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  useState,
} from 'react';
import type { AdminResourceType } from 'types/api/admin/global_search';
import type { ResourceType } from 'types/models';
import { adminResourcePaths } from 'utils/adminResourcePaths';
import { ImpersonateButton } from './ImpersonateButton';

const ICON_SIZE = 18;

// useAutocomplete derives the input id (`${AUTOCOMPLETE_ID}`) and listbox id
// (`${AUTOCOMPLETE_ID}-listbox`) from this; we reuse both to move focus by keyboard.
const AUTOCOMPLETE_ID = 'admin-global-search';

// Result types whose owner role can be impersonated straight from the palette.
// Each maps 1:1 to a backend ResourceType, so `impersonation_target` resolves the owner role.
const IMPERSONABLE_TYPES = ['Lab', 'Group', 'Pharmacy'] as const satisfies ResourceType[];
type ImpersonableType = (typeof IMPERSONABLE_TYPES)[number];
const isImpersonable = (type: AdminResourceType): type is ImpersonableType =>
  (IMPERSONABLE_TYPES as readonly AdminResourceType[]).includes(type);

// Same icons as the admin navbar entries.
const typeIcons: Record<AdminResourceType, ReactNode> = {
  Lab: <SvgIcon name="ic_lab" size={ICON_SIZE} />,
  Pharmacy: <SvgIcon name="ic_pharma" size={ICON_SIZE} />,
  Group: <SvgIcon name="ic_group" size={ICON_SIZE} />,
  User: <SvgIcon name="ic_user" size={ICON_SIZE} />,
  Invoice: <WarningIconMUI2 sx={{ fontSize: ICON_SIZE }} />,
  ExpiredProductsRequest: <SvgIcon name="ic_expired" size={ICON_SIZE} />,
  Trade: <OfferIconMUI sx={{ fontSize: ICON_SIZE }} />,
};

const Kbd = ({ children }: { children: ReactNode }) => (
  <Box
    component="kbd"
    sx={{
      px: 0.75,
      borderRadius: 0.5,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'background.neutral',
      typography: 'caption',
      fontFamily: 'inherit',
    }}
  >
    {children}
  </Box>
);

export const GlobalSearchPalette = () => {
  const [isOpen, setIsOpen] = useState(false);

  useKeyboardShortcut({ key: 'k', cmdOrCtrl: true }, () => setIsOpen(true));

  // Mounted only while open: useAutocomplete expects its input to exist, and the search resets on close.
  return isOpen && <OpenGlobalSearchPalette close={() => setIsOpen(false)} />;
};

const OpenGlobalSearchPalette = ({ close }: { close: VoidFunction }) => {
  const [search, setSearch, searchDebounced] = useSearchState();
  const navigate = useNavigate();

  const query = searchDebounced.trim();
  const searchQuery = useIndexQuery('admin/global_search', { query }, { enabled: !!query });
  // Hide results while the debounce is pending, so Enter never opens a result of the previous query.
  const isUpToDate = search.trim() === query;
  const results = (isUpToDate && searchQuery.data?.results) || [];
  const isLoading = !!search.trim() && (!isUpToDate || searchQuery.isFetching);

  const typeLabels: Record<AdminResourceType, string> = {
    Lab: t`Laboratoires`,
    Pharmacy: t`Pharmacies`,
    Group: t`Groupements`,
    User: t`Utilisateurs`,
    Invoice: t`Réclamations`,
    ExpiredProductsRequest: t`Périmés`,
    Trade: t`Trades`,
  };

  const { getRootProps, getInputProps, getListboxProps, getOptionProps } = useAutocomplete({
    id: AUTOCOMPLETE_ID,
    open: !!search.trim(),
    // Escape is swallowed by the autocomplete while it is open: close the palette ourselves.
    onClose: (_, reason) => reason === 'escape' && close(),
    options: results,
    filterOptions: (options) => options,
    getOptionLabel: (result) => result.label,
    isOptionEqualToValue: (option, value) => option.type === value.type && option.id === value.id,
    inputValue: search,
    onInputChange: (_, value, reason) => reason === 'input' && setSearch(value),
    onChange: (_, result) => {
      if (!result) return;
      close();
      navigate({ to: adminResourcePaths[result.type], params: { id: result.id } });
    },
    autoHighlight: true,
  });

  const hasNoResult = !!search.trim() && !isLoading && results.length === 0;

  const focusInput = () => document.getElementById(AUTOCOMPLETE_ID)?.focus();

  // ArrowRight hands real focus to the highlighted row's Impersonate button (the row
  // highlight is only a `.Mui-focused` class — focus stays on the input). Handled on the
  // InputBase root so autocomplete's own input onKeyDown (arrow up/down) is left intact —
  // so the keydown originates on the input, read it via `event.target` (currentTarget is
  // the root div). Only hijack when the caret is at the end, so ArrowRight still moves the
  // caret while editing the query.
  const onInputKeyDown = (event: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key !== 'ArrowRight') return;
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.selectionStart !== input.value.length || input.selectionEnd !== input.value.length)
      return;
    const button = document.querySelector(`#${AUTOCOMPLETE_ID}-listbox .Mui-focused button`);
    if (button instanceof HTMLElement) {
      event.preventDefault();
      button.focus();
    }
  };

  // While the button is focused, keep keystrokes from bubbling to the autocomplete root:
  // it handles Enter as "select the highlighted row" (navigating away) and would swallow
  // the button's own Enter→click. Native click on the focused button still fires.
  const onButtonKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    event.stopPropagation();
    if (event.key === 'ArrowLeft' || event.key === 'Escape') {
      event.preventDefault();
      focusInput();
    }
  };

  return (
    <Modal
      open
      onClose={close}
      // Otherwise the focus trap focuses the Paper on open, stealing focus from the autofocused input.
      disableAutoFocus
      slotProps={{ backdrop: { invisible: true } }}
    >
      <Paper
        {...getRootProps()}
        sx={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: (theme) => `calc(100% - ${theme.spacing(4)})`,
          maxWidth: (theme) => theme.breakpoints.values.sm,
          borderRadius: 2,
          overflow: 'hidden',
          // Both shadows stacked + a border: the backdrop is invisible, so only these separate the palette from the page.
          border: 1,
          borderColor: 'divider',
          boxShadow: (theme) => `${theme.shadows[24]}, ${theme.customShadows.z24}`,
          outline: 'none',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 2.5, py: 2 }}>
          <SearchIconMUI color="disabled" />
          <InputBase
            autoFocus
            fullWidth
            placeholder={t`Rechercher par nom, ID, CIP ou téléphone`}
            inputProps={getInputProps()}
            onKeyDown={onInputKeyDown}
            sx={{ typography: 'subtitle1' }}
          />
          {isLoading && <CircularProgress size={ICON_SIZE} />}
        </Stack>

        {results.length > 0 && (
          <Box
            component="ul"
            {...getListboxProps()}
            sx={{
              m: 0,
              px: 1,
              pb: 1,
              listStyle: 'none',
              maxHeight: '50vh',
              overflowY: 'auto',
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            {/* The backend returns results contiguous by type: a header at each type change is enough. */}
            {results.map((result, index) => {
              // getOptionProps returns the key: React needs it passed directly, not spread.
              const { key: optionKey, ...optionProps } = getOptionProps({ option: result, index });

              return (
                <Fragment key={`${result.type}-${result.id}`}>
                  {result.type !== results[index - 1]?.type && (
                    <Typography
                      component="li"
                      variant="overline"
                      color="text.disabled"
                      sx={{ display: 'block', px: 1.5, pt: 1.5, pb: 0.5 }}
                    >
                      {typeLabels[result.type]}
                    </Typography>
                  )}
                  <Stack
                    component="li"
                    key={optionKey}
                    {...optionProps}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      color: 'text.secondary',
                      '&.Mui-focused': { bgcolor: 'action.selected', color: 'text.primary' },
                    }}
                  >
                    <Stack
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        width: (theme) => theme.spacing(4),
                        height: (theme) => theme.spacing(4),
                        borderRadius: 1,
                        bgcolor: 'background.neutral',
                        flexShrink: 0,
                      }}
                    >
                      {typeIcons[result.type]}
                    </Stack>
                    <Typography variant="body2" color="text.primary" noWrap sx={{ flex: 1 }}>
                      {result.label}
                    </Typography>
                    {isImpersonable(result.type) && (
                      // Stop propagation: the row is an autocomplete option whose click navigates.
                      <Box
                        onClick={(event) => event.stopPropagation()}
                        onMouseDown={(event) => event.stopPropagation()}
                        onKeyDown={onButtonKeyDown}
                      >
                        <ImpersonateButton
                          resourceType={result.type}
                          resourceId={result.id}
                          onImpersonated={close}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    )}
                    <Typography variant="caption" color="text.disabled">
                      #{result.id}
                    </Typography>
                  </Stack>
                </Fragment>
              );
            })}
          </Box>
        )}

        {hasNoResult && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ py: 4, borderTop: 1, borderColor: 'divider' }}
          >
            {t`Aucun résultat pour « ${search.trim()} »`}
          </Typography>
        )}

        <Stack
          direction="row"
          spacing={2}
          sx={{
            px: 2.5,
            py: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.neutral',
            color: 'text.disabled',
            typography: 'caption',
          }}
        >
          <span>
            <Kbd>↑</Kbd> <Kbd>↓</Kbd> {t`naviguer`}
          </span>
          <span>
            <Kbd>↵</Kbd> {t`ouvrir`}
          </span>
          <span>
            <Kbd>→</Kbd> {t`impersonnifier`}
          </span>
          <span>
            <Kbd>Échap</Kbd> {t`fermer`}
          </span>
        </Stack>
      </Paper>
    </Modal>
  );
};
